// client/src/pages/labEngine/notebookSimulator.jsx

import React, { useState, useEffect, useContext, useCallback, useRef,
  forwardRef, useImperativeHandle } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import { Plus, Download, Play, Trash2 } from 'lucide-react';
import showNotification from '../../utilities/showNotification';
import ProfileHeader from '../../components/headers/profileHeader';
import SharedHeader from '../../components/headers/sharedHeader1';
import CustomButton from '../../components/customs/customButton';
import BackButton from '../../components/customs/backButton';
import ProfIXChatbot from '../../components/chatBot/ProfIXChatbot';
import NotebookCell from '../../components/labEngine/NotebookCell';
import { PyodideProvider, usePyodide } from '../../components/labEngine/PyodideContext';
import { NodeJSProvider, useNodeJS }   from '../../components/labEngine/NodeJSContext';
import { CourseDomains }               from '../../types/courseDomains';
import SimulationReport from '../../modals/labEngine/simulationReport';  // reuse existing


import { GlobalContext } from '../../context/provider';
import { useVisualCircuit, useSimulator, useComponentLibrary } from '../../hooks';
import useCourse from '../../hooks/useCourse';

import '../../styles/globalstyles.css';
import './notebookSimulator.css';

// ── Helpers (outside component to avoid recreation) ───────────────────────
const generateId = () => `cell_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const DEFAULT_CELLS = [
  {
    id: generateId(),
    code: `# Welcome to the AI Lab\nimport numpy as np\nimport pandas as pd\nprint("NumPy:", np.__version__)\nprint("Pandas:", pd.__version__)`,
    simOutputs: [], tag: null, label: 'Welcome', componentId: '',
  },
  {
    id: generateId(),
    code: `# Matplotlib example\nimport matplotlib.pyplot as plt\nimport numpy as np\n\nx = np.linspace(0, 2 * np.pi, 100)\nplt.figure(figsize=(7, 3))\nplt.plot(x, np.sin(x), label='sin(x)', color='#3b82f6')\nplt.plot(x, np.cos(x), label='cos(x)', color='#f59e0b')\nplt.legend()\nplt.title('Sine and Cosine')\nplt.tight_layout()\nplt.show()`,
    simOutputs: [], tag: null, label: 'Plot Example', componentId: '',
  },
];

// ── Inner notebook component (needs PyodideContext) ───────────────────────
const NotebookInner = forwardRef (({
  notebookTitle = 'Untitled Notebook',
  simulatorId = null,
  initialCells,
  domain,
  externalLoading,
  emptyMessage,
  executionContext = 'pyodide',
  onSaveCell,
  onRegenerateCell,
  onMove,
  onTranslateCell,
  onRetryGenerateCell,
  onCellOutput,
}, ref) => {

  const cellEditorRefs = useRef({});
  // Refs to each NotebookCell's own imperative handle (run/clearOutput) —
  // separate from cellEditorRefs above, which holds the raw Monaco editor
  // instances instead.
  const cellInstanceRefs = useRef({});

  const [cells, setCells] = useState(() => {
    if (initialCells && initialCells.length > 0) {
      return initialCells.map(c => ({
        id:                c.cellId || generateId(),
        code:              c.code   || c.source || '',
        simOutputs:        c.simOutputs  || [],
        tag:               c.tag         || null,
        label:             c.label       || '',
        category:          c.category    || '',
        componentId:       c.componentId || '',
        javascriptCode:    c.javascriptCode || '',
        pythonCode:        c.pythonCode     || c.code || '',
        isPyodideSkeleton: c.isPyodideSkeleton || false,
      }));
    }
    return DEFAULT_CELLS;
  });

  // const { isLoading, error } = usePyodide();
  const pyodideCtx = usePyodide();
  const nodejsCtx  = useNodeJS();
  const activeCtx  = executionContext === 'nodejs' ? nodejsCtx : pyodideCtx;
  const { isLoading, error } = activeCtx || {};
  const [title, setTitle] = useState(notebookTitle);
  const [regeneratingCellId, setRegeneratingCellId] = useState(null);

  // ── Expose cell codes globally for pin validation in NotebookCell ─────────
  useEffect(() => {
    window.__notebookCellCodes = cells.map(c => ({ id: c.id, code: c.code }));
  }, [cells]);

  useImperativeHandle(ref, () => ({
    updateCellCode: (cellId, newCode) => {
      // Freshly-regenerated Python invalidates any previous JS translation —
      // clear it so NotebookCell's own resync effect (keyed on the
      // javascriptCode prop) picks up the change and requires a fresh
      // Translate before Run, instead of silently keeping stale JS around.
      setCells(prev => prev.map(c =>
        c.id === cellId ? { ...c, code: newCode, javascriptCode: '' } : c
      ));
      const editor = cellEditorRefs.current[cellId];
      if (editor) editor.getModel()?.setValue(newCode);
    }
  }));

  const addCell = useCallback((afterId) => {
    const newCell = { id: generateId(), code: '', simOutputs: [], tag: null, label: '', componentId: '' };
    setCells(prev => {
      if (!afterId) return [...prev, newCell];
      const idx = prev.findIndex(c => c.id === afterId);
      const next = [...prev];
      next.splice(idx + 1, 0, newCell);
      return next;
    });
  }, []);

  const deleteCell = useCallback((id) => {
    setCells(prev => prev.length === 1 ? prev : prev.filter(c => c.id !== id));
  }, []);

  const updateCode = useCallback((id, code) => {
    setCells(prev => prev.map(c => c.id === id ? { ...c, code } : c));
  }, []);

  const updateJsCode = useCallback((id, jsCode) => {
    setCells(prev => prev.map(c => c.id === id ? { ...c, javascriptCode: jsCode } : c));
  }, []);

  const handleCellOutput = useCallback((cellId, componentId, output) => {
    setCells(prev => prev.map(c =>
      c.id === cellId ? {
        ...c,
        simOutputs: {
          stdout:  output.stdout  || '',
          error:   output.error   || '',
          hasPlot: !!output.plot,
          ranAt:   new Date(),
        }
      } : c
    ));
    onCellOutput?.(cellId, componentId, output);
  }, [onCellOutput]);

  // Runs every cell in order, awaiting each before starting the next —
  // stops at the first cell whose run didn't succeed (error, or blocked by
  // a guard like "no JS yet"), rather than piling on more failures past it.
  const runAllCells = useCallback(async () => {
    for (const cell of cells) {
      const instance = cellInstanceRefs.current[cell.id];
      if (!instance?.run) continue;
      const { ok } = await instance.run();
      if (!ok) {
        showNotification(
          `Run All stopped at "${cell.label || cell.id}" — fix the issue and try again.`,
          'error'
        );
        return;
      }
    }
  }, [cells]);

  // Clears what's displayed in each cell right now — does not touch the
  // saved simulator record, so reloading the page brings the old results
  // back (matches Run's own display-vs-persisted split).
  const clearAllResults = useCallback(() => {
    cells.forEach(cell => {
      cellInstanceRefs.current[cell.id]?.clearOutput();
    });
  }, [cells]);

  const moveCell = useCallback((id, direction) => {
    setCells(prev => {
      const idx = prev.findIndex(c => c.id === id);
      if (idx === -1) return prev;
      if (direction === 'up' && idx === 0) return prev;
      if (direction === 'down' && idx === prev.length - 1) return prev;
      const next = [...prev];
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
      onMove?.(next.map(c => c.id));
      return next;
    });
  }, [onMove]);

  const downloadNotebook = () => {
    const ipynb = {
      nbformat: 4, nbformat_minor: 5,
      metadata: {
        kernelspec: executionContext === 'nodejs'
          ? { display_name: 'Node.js', language: 'javascript', name: 'nodejs' }
          : { display_name: 'Python 3', language: 'python', name: 'python3' },
        language_info: executionContext === 'nodejs'
          ? { name: 'javascript', version: '18' }
          : { name: 'python', version: '3.11' },
      },
      cells: cells.map(c => ({
        cell_type: 'code',
        source: c.code.split('\n'),
        metadata: {}, outputs: [], execution_count: null,
      })),
    };
    const blob = new Blob([JSON.stringify(ipynb, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/\s+/g, '_')}.ipynb`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  const downloadAllJs = () => {
    const missing = cells.filter(c => !c.javascriptCode?.trim());
    if (missing.length > 0) {
      showNotification(
        `${missing.length} cell(s) not translated yet — download will skip them.`,
        'error'
      );
    }

    const combined = cells
      .filter(c => c.javascriptCode?.trim())
      .map((c, i) => {
        const header = `// ── Cell ${i + 1}${c.label ? `: ${c.label}` : ''}${c.componentId ? ` [${c.componentId}]` : ''} ──`;
        return `${header}\n${c.javascriptCode}`;
      })
      .join('\n\n');

    const blob = new Blob([combined], { type: 'text/javascript' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/\s+/g, '_')}.js`;
    a.click();
    URL.revokeObjectURL(url);
  };
 
  const downloadAllResults = () => {
    const anyRun = cells.some(c => c.simOutputs && (c.simOutputs.stdout || c.simOutputs.error || c.simOutputs.hasPlot));
    if (!anyRun) {
      showNotification('No cells have been run yet.', 'error');
      return;
    }

    const combined = cells.map((c, i) => {
      const header = `## Cell ${i + 1}${c.label ? `: ${c.label}` : ''}${c.componentId ? ` [${c.componentId}]` : ''}`;
      const out = c.simOutputs;
      if (!out || (!out.stdout && !out.error && !out.hasPlot)) {
        return `${header}\n(not run)`;
      }
      let body = '';
      if (out.stdout)  body += out.stdout + '\n';
      if (out.error)   body += `ERROR:\n${out.error}\n`;
      if (out.hasPlot) body += '[plot image omitted from text export]\n';
      return `${header}\n${body}`;
    }).join('\n---\n\n');

    const blob = new Blob([combined], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/\s+/g, '_')}_results.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="nb-pyodide-loading">
        <div className="nb-pyodide-spinner" />
        <div className="nb-pyodide-loading-text">
          <p>Starting {executionContext === 'nodejs' ? 'JavaScript' : 'Python'} environment</p>
          <p>🤖 Prof-iX is generating the code …</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="nb-pyodide-error">
        <strong>Failed to load {executionContext === 'nodejs' ? 'JavaScript' : 'Python'} environment:</strong> {error}
      </div>
    );
  }

  if (!isLoading && !externalLoading && cells.length === 0 && emptyMessage) {
    return (
      <div className="nb-empty-state">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="nb-inner-wrap">

      {/* Header */}
      <div className="nb-header">
        <div className="nb-header-left">
          <div className="nb-header-logo">ProfiX Labs</div>
          {/* <span className="nb-header-kernel-badge">
            {executionContext === 'nodejs' ? '● Node.js' : '● Python 3.11'}
          </span> */}
        </div>
        <button onClick={downloadNotebook} className="nb-header-download-btn">
          <Download size={14} /> Download .ipynb
        </button>
        {executionContext === 'nodejs' && (
          <button onClick={downloadAllJs} className="nb-header-download-btn">
            <Download size={14} /> Download .js
          </button>
        )}
        <button onClick={downloadAllResults} className="nb-header-download-btn">
          <Download size={14} /> Download Results
        </button>
        <button onClick={runAllCells} className="nb-header-download-btn">
          <Play size={14} /> Run All
        </button>
        <button onClick={clearAllResults} className="nb-header-download-btn">
          <Trash2 size={14} /> Clear All Results
        </button>
      </div>

      {/* Cells */}
      {cells.map((cell, index) => (
        <NotebookCell
          key={cell.id}
          ref={(el) => { cellInstanceRefs.current[cell.id] = el; }}
          id={cell.id}
          simulatorId={simulatorId}
          index={index}
          domain={domain}
          category={cell.category || ''}
          executionContext={executionContext}
          initialCode={cell.code}
          javascriptCode={cell.javascriptCode || ''}
          simOutputs={cell.simOutputs || []}
          tag={cell.tag || null}
          label={cell.label || ''}
          componentId={cell.componentId || ''}
          savedCode={cell.code}
          onSaveCell={onSaveCell}
          // onRegenerateCell={onRegenerateCell}
          onRegenerateCell={async (cellId) => {
            setRegeneratingCellId(cellId);
            try {
              await onRegenerateCell(cellId);
            } finally {
              setRegeneratingCellId(null);
            }
          }}
          isRegenerating={regeneratingCellId === cell.id}
          onDelete={deleteCell}
          onInsertBelow={addCell}
          onCodeChange={updateCode}
          onMove={moveCell}
          isLast={index === cells.length - 1}
          onEditorMount={(editor) => {
            cellEditorRefs.current[cell.id] = editor;
          }}
          onTranslateCell={onTranslateCell}
          onJsCodeChange={updateJsCode}
          onCellOutput={handleCellOutput}
          generationStatus={cell.generationStatus}
          generationError={cell.generationError}
          onRetryGenerateCell={onRetryGenerateCell}
        />
      ))}

      {/* Add cell */}
      <button onClick={() => addCell()} className="nb-add-cell-btn">
        <Plus size={14} /> Add cell
      </button>

      <p className="nb-footer-hint">
        Shift+Enter to run a cell · Variables persist across cells in this session
      </p>

    </div>
  );

});

// ── Outside and above NotebookSimulator ──────────────────────────────────────
const NotebookProvider = ({ executionContext, children, executeNotebookCell }) => {
  if (executionContext === 'nodejs') {
    return (
      <NodeJSProvider executeNotebookCell={executeNotebookCell}>
        {children}
      </NodeJSProvider>
    );
  }
  return <PyodideProvider>{children}</PyodideProvider>;
};

// ── Main page component ───────────────────────────────────────────────────
const NotebookSimulator = () => {

  const { courseId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');
  const navigate  = useNavigate();

  const cellEditorRefs = useRef({});
  const notebookRef = useRef(null);
  
  // ── State ─────────────────────────────────────────────────────────────────
  const [circuit,       setCircuit]       = useState(null);
  const [simulator,     setSimulator]     = useState(null);
  const [notebookCells, setNotebookCells] = useState([]);
  const [courseInfo,    setCourseInfo]    = useState(null);
  const [isGeneratingCells, setIsGeneratingCells] = useState(false);
  const [isLoading,         setIsLoading]         = useState(true);
  const [showSimulationReport, setShowSimulationReport] = useState(false);
  const [cellOutputs, setCellOutputs] = useState([]);  // collected from NotebookCell runs
  const [generationProgress, setGenerationProgress] = useState(null); 
  
  const executionContext = CourseDomains[simulator?.domain]?.executionContext || 'pyodide';
  // ── Hooks ─────────────────────────────────────────────────────────────────
  const {
    getCircuitBySessionId,
    updateNotebookOrder,
    error: circuitError,
    clearError: clearCircuitError,
  } = useVisualCircuit();

  const {
    getSimulatorBySessionId,
    generateNotebookCells,
    translateNotebookCells,
    executeNotebookCell,
    saveNotebookCells,
    saveGradingPayload, 
    submitGrades,  
    resetSimulator,
  } = useSimulator();

  const { getComponentById } = useComponentLibrary();
  const { getCourseById }    = useCourse();

  const { authState: { authData } } = useContext(GlobalContext);

  // ── Load data ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const loadData = async () => {
      try {
        if (!authData?.id) return;

        if (!sessionId) {
          console.error('❌ No sessionId in URL');
          alert('⚠️ Invalid notebook link. SessionId is required.');
          return;
        }

        // 1. Load course info
        if (courseId) {
          const course = await getCourseById(courseId);
          if (course.success) setCourseInfo(course.data);
        }

        // 2. Load simulator by sessionId
        const simResponse = await getSimulatorBySessionId(sessionId);
        if (!simResponse.success) {
          console.error('❌ Simulator not found for sessionId:', sessionId);
          alert('⚠️ Simulator not found. Please sync your circuit first.');
          navigate(-1);
          return;
        }
        setSimulator(simResponse.data);
        console.log('✅ Simulator loaded:', simResponse.data.simulatorId);

        // 3. Load circuit by sessionId
        const circuitResponse = await getCircuitBySessionId(sessionId);
        console.log('🔍 Circuit response:', circuitResponse);
        if (!circuitResponse.success) {
          console.error('❌ Failed to load circuit:', circuitResponse.error);
          alert(`⚠️ Failed to load circuit: ${circuitResponse.error || 'Unknown error'}`);
          return;
        }

        // 4. Enrich each component with componentLibraryData
        const enrichedCircuit = { ...circuitResponse.data };
        enrichedCircuit.components = await Promise.all(
          circuitResponse.data.components.map(async (comp) => {
            if (comp.componentId) {
              try {
                const libResponse = await getComponentById(comp.componentId);
                if (libResponse?.success) {
                  return { ...comp, componentLibraryData: libResponse.data };
                }
              } catch (err) {
                console.error('Error fetching component library data:', err);
              }
            }
            return comp;
          })
        );
        setCircuit(enrichedCircuit);

        // 5. Load or generate notebook cells — cell by cell, resumable
        setIsGeneratingCells(false); // no more blocking full-page spinner
        try {
          const savedCells = simResponse.data.notebookCells || [];

          const orderedComponents = [...enrichedCircuit.components].sort((a, b) => {
            const ao = a.notebookOrder ?? Infinity;
            const bo = b.notebookOrder ?? Infinity;
            return ao - bo;
          });

          // A circuit can have MORE THAN ONE AG_GRAPH_BUILDER (e.g. a
          // subgraph composed from a second, independent graph) — each one
          // has its own sub-canvas that needs its own cells spliced in at
          // its own position, not just the first Builder found.
          const allGraphBuilders = enrichedCircuit.components.filter(c => c.langGraphType === 'builder');

          // Most cells (tools, LLMs, graph nodes) live inside a Builder's own
          // graphFlow.nodes, not in enrichedCircuit.components — apply the
          // same notebookOrder sort used above for top-level components, or
          // reordering these via drag/move never visibly persists across a
          // reload. Keyed by Builder instanceId so each graph's cells stay
          // scoped to that graph.
          const subCanvasNodesByBuilder = new Map(
            allGraphBuilders.map(b => [
              b.instanceId,
              [...(b.graphFlow?.nodes || [])].sort((a, c) => (a.notebookOrder ?? Infinity) - (c.notebookOrder ?? Infinity)),
            ])
          );

          const makeInitialCell = (comp) => {
            const cellId = `cell_${comp.instanceId}`;
            const saved = savedCells.find(c => c.cellId === cellId);
            const hasCode = saved && (saved.pythonCode || saved.code);
            const freshLabel = comp.label || comp.componentName;
            return hasCode
              ? { ...saved, label: freshLabel, generationStatus: 'done' }
              : {
                  cellId,
                  instanceId:       comp.instanceId,
                  componentId:      comp.componentId,
                  label:            freshLabel,
                  category:         comp.category,
                  code: '', pythonCode: '', javascriptCode: '',
                  generationStatus: 'pending',
                };
          };

          const subCanvasInitialCellsByBuilder = new Map(
            allGraphBuilders.map(b => [
              b.instanceId,
              (subCanvasNodesByBuilder.get(b.instanceId) || []).map(makeInitialCell),
            ])
          );

          // Flattened views, used where the code below previously assumed a
          // single Builder's worth of sub-canvas nodes/cells.
          const subCanvasNodes = allGraphBuilders.flatMap(b => subCanvasNodesByBuilder.get(b.instanceId) || []);
          const subCanvasInitialCells = allGraphBuilders.flatMap(b => subCanvasInitialCellsByBuilder.get(b.instanceId) || []);

          // Single-pass build — sub-canvas cells inserted exactly when the
          // builder is reached, instead of a separate findIndex/splice
          // that can silently miss and fall back to appending at the end.
          // const initialCells = [];
          // orderedComponents.forEach(comp => {
          //   if (graphBuilderComp && comp.instanceId === graphBuilderComp.instanceId) {
          //     initialCells.push(...subCanvasInitialCells);
          //   }

          //   const cellId = `cell_${comp.instanceId}`;
          //   const saved = savedCells.find(c => c.cellId === cellId);
          //   const hasCode = saved && (saved.pythonCode || saved.code);
          //   initialCells.push(
          //     hasCode
          //       ? { ...saved, generationStatus: 'done' }
          //       : {
          //           cellId,
          //           instanceId:        comp.instanceId,
          //           componentId:       comp.componentId,
          //           label:             comp.label || comp.componentName,
          //           category:          comp.category,
          //           code:              '',
          //           pythonCode:        '',
          //           javascriptCode:    '',
          //           generationStatus:  'pending',
          //         }
          //   );
          // });

          const initialCells = [];
          const splicedBuilderIds = new Set();
          orderedComponents.forEach(comp => {
            if (comp.langGraphType === 'builder' && subCanvasInitialCellsByBuilder.has(comp.instanceId)) {
              initialCells.push(...(subCanvasInitialCellsByBuilder.get(comp.instanceId) || []));
              splicedBuilderIds.add(comp.instanceId);
            }

            const cellId = `cell_${comp.instanceId}`;
            const saved = savedCells.find(c => c.cellId === cellId);
            const hasCode = saved && (saved.pythonCode || saved.code);
            const freshLabel = comp.label || comp.componentName;

            initialCells.push(
              hasCode
                ? { ...saved, label: freshLabel, generationStatus: 'done' }   // ← override with fresh label
                : {
                    cellId,
                    instanceId:        comp.instanceId,
                    componentId:       comp.componentId,
                    label:             freshLabel,
                    category:          comp.category,
                    code:              '',
                    pythonCode:        '',
                    javascriptCode:    '',
                    generationStatus:  'pending',
                  }
            );
          });

          // Safety net: any Builder that never turned up in orderedComponents
          // (shouldn't normally happen — a Builder is always also a top-level
          // component) still gets its sub-canvas cells included, rather than
          // silently dropped.
          allGraphBuilders.forEach(b => {
            if (!splicedBuilderIds.has(b.instanceId)) {
              initialCells.push(...(subCanvasInitialCellsByBuilder.get(b.instanceId) || []));
            }
          });

          setNotebookCells(initialCells);

          // pendingComponents intentionally excludes sub-canvas nodes —
          // they're generated as a side-effect only when the builder
          // itself generates (backend expansion splices their cells into
          // the same API response), not requested independently here.
          // const pendingComponents = orderedComponents.filter(comp =>
          //   initialCells.find(c => c.cellId === `cell_${comp.instanceId}`)?.generationStatus === 'pending'
          // );

          // Subcanvas nodes are now generated one at a time, each with its
          // own AI call — same as top-level components — to avoid a single
          // oversized call exceeding the model's output token limit.
          const pendingSubCanvasByBuilder = new Map(
            allGraphBuilders.map(b => [
              b.instanceId,
              (subCanvasNodesByBuilder.get(b.instanceId) || []).filter(n =>
                initialCells.find(c => c.cellId === `cell_${n.instanceId}`)?.generationStatus === 'pending'
              ),
            ])
          );
          const pendingTopLevelComponents = orderedComponents.filter(comp =>
            initialCells.find(c => c.cellId === `cell_${comp.instanceId}`)?.generationStatus === 'pending'
          );
          // Interleave: generate each Builder's own pending subcanvas nodes
          // right before that Builder's own cell, matching their position
          // in initialCells — scoped per-Builder, not dumped all at once at
          // whichever Builder happens to come first.
          const pendingComponents = [];
          const splicedPendingBuilderIds = new Set();
          pendingTopLevelComponents.forEach(comp => {
            if (comp.langGraphType === 'builder' && pendingSubCanvasByBuilder.has(comp.instanceId)) {
              pendingComponents.push(...(pendingSubCanvasByBuilder.get(comp.instanceId) || []));
              splicedPendingBuilderIds.add(comp.instanceId);
            }
            pendingComponents.push(comp);
          });
          allGraphBuilders.forEach(b => {
            if (!splicedPendingBuilderIds.has(b.instanceId)) {
              pendingComponents.push(...(pendingSubCanvasByBuilder.get(b.instanceId) || []));
            }
          });

          if (pendingComponents.length === 0) {
            showNotification('Notebook loaded!', 'success');
          } else {
            await generateCellsSequentially(pendingComponents, initialCells, enrichedCircuit, simResponse.data);
          }
        } catch (err) {
          console.error('Error generating/loading cells:', err);
          showNotification('Failed to load notebook cells', 'error');
        } finally {
          setIsGeneratingCells(false);
        }

      } catch (err) {
        console.error('❌ Error loading AI notebook page:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [sessionId, courseId, authData?.id]);

  const generateCellsSequentially = async (componentsToGenerate, currentCells, circuit, simulator) => {
    let workingCells = currentCells;

    // AgenticAI: sub-canvas node/edge cells ride along with the builder's
    // own generation call but never appear in componentsToGenerate — count
    // the still-pending ones up front so the progress total/current reflect
    // every cell actually produced, not just the flat top-level components.
    // const graphBuilderComp = circuit.components.find(c => c.langGraphType === 'builder');
    // const subCanvasNodes = graphBuilderComp?.graphFlow?.nodes || [];
    // const pendingSubCanvasCount = subCanvasNodes.filter(n => {
    //   const cell = currentCells.find(c => c.cellId === `cell_${n.instanceId}`);
    //   return !cell || cell.generationStatus === 'pending';
    // }).length;

    // const totalCount = componentsToGenerate.length + pendingSubCanvasCount;

    // componentsToGenerate now already includes subcanvas nodes directly
    // (each generated with its own call), so no separate side-effect count
    // is needed.
    const totalCount = componentsToGenerate.length;
    let cellsCompleted = 0;

    for (let i = 0; i < componentsToGenerate.length; i++) {
      const comp = componentsToGenerate[i];
      const cellId = `cell_${comp.instanceId}`;

      workingCells = workingCells.map(c =>
        c.cellId === cellId ? { ...c, generationStatus: 'generating' } : c
      );
      setNotebookCells(workingCells);
      setGenerationProgress({
        current: cellsCompleted + 1,
        total: totalCount,
        label: comp.label || comp.componentName,
      });

      try {
        const response = await generateNotebookCells(
          [comp.componentId],
          circuit.components,
          simulator?.domain,
          circuit.connections,
          workingCells,
          sessionId,
          [comp.instanceId],
          authData?.role
        );

        // The builder's call returns itself PLUS its sub-canvas node/edge
        // cells in the same array — merge all of them, not just one.
        const newCells = response.success ? response.data.cells : [];

        if (response.success && newCells.length > 0) {
          newCells.forEach((nc) => {
            const ncId = `cell_${nc.instanceId}`;
            const exists = workingCells.some(c => c.cellId === ncId);
            workingCells = exists
              ? workingCells.map(c => c.cellId === ncId ? { ...nc, generationStatus: 'done' } : c)
              : [...workingCells, { ...nc, generationStatus: 'done' }];
          });
          cellsCompleted += newCells.length;
        } else {
          workingCells = workingCells.map(c =>
            c.cellId === cellId
              ? { ...c, generationStatus: 'error', generationError: response.error || 'Generation failed' }
              : c
          );
          cellsCompleted += 1;
        }
      } catch (err) {
        console.error(`❌ Error generating cell ${cellId}:`, err);
        workingCells = workingCells.map(c =>
          c.cellId === cellId
            ? { ...c, generationStatus: 'error', generationError: err.message }
            : c
        );
        cellsCompleted += 1;
      }

      setNotebookCells(workingCells);
      // ← the resumability guarantee: persist after EVERY cell, not at the end
      await saveNotebookCells(simulator._id, workingCells);
    }

    setGenerationProgress(null);
    const anyErrors = workingCells.some(c => c.generationStatus === 'error');
    showNotification(
      anyErrors ? 'Notebook generated with some errors — retry failed cells' : 'Notebook generated!',
      anyErrors ? 'error' : 'success'
    );
  };

  // ── Retry a single failed cell without touching any others.
  const retryGenerateCell = async (cellId) => {
    if (!simulator) return;

    // Re-fetch fresh, same as handleRegenerateCell — the in-memory `circuit`
    // state can predate a config change (e.g. childBuilderInstanceId) made
    // since this tab was last loaded, causing the server to generate off
    // stale component data.
    const freshCircuitResponse = await getCircuitBySessionId(sessionId);
    if (!freshCircuitResponse.success) {
      showNotification('Could not refresh circuit before regenerating', 'error');
      return;
    }
    const freshCircuit = freshCircuitResponse.data;
    setCircuit(prev => ({ ...prev, ...freshCircuit }));

    let comp = freshCircuit.components.find(c => `cell_${c.instanceId}` === cellId);

    if (!comp) {
      // Sub-canvas node/edge cell — target it directly, same as
      // handleRegenerateCell, so only this one node is regenerated. Search
      // every Builder's sub-canvas, not just the first — a circuit can have
      // more than one AG_GRAPH_BUILDER.
      comp = freshCircuit.components
        .filter(c => c.langGraphType === 'builder')
        .flatMap(b => b.graphFlow?.nodes || [])
        .find(n => `cell_${n.instanceId}` === cellId) || null;
    }

    if (!comp) return;
    await generateCellsSequentially([comp], notebookCells, freshCircuit, simulator);
  };

  const handleRegenerateCell = async (cellId) => {
    if (!circuit || !simulator) return;

    const freshCircuitResponse = await getCircuitBySessionId(sessionId);
    if (!freshCircuitResponse.success) {
      showNotification('Could not refresh circuit before regenerating', 'error');
      return;
    }
    const freshCircuit = freshCircuitResponse.data;
    setCircuit(prev => ({ ...prev, ...freshCircuit })); // keep state in sync too

    let comp = freshCircuit.components.find(
      c => `cell_${c.instanceId}` === cellId
    );

    if (!comp) {
      // Sub-canvas node/edge cell — target it directly (its own
      // componentId + instanceId), not the builder. The backend detects
      // an instanceId that lives inside a builder's graphFlow.nodes and
      // generates just that single node. Search every Builder's sub-canvas,
      // not just the first — a circuit can have more than one
      // AG_GRAPH_BUILDER.
      comp = freshCircuit.components
        .filter(c => c.langGraphType === 'builder')
        .flatMap(b => b.graphFlow?.nodes || [])
        .find(n => `cell_${n.instanceId}` === cellId) || null;
    }

    if (!comp) {
      console.error('❌ No component found for cellId:', cellId);
      return;
    }

    const response = await generateNotebookCells(
      [comp.componentId],
      freshCircuit.components,      // ← fresh, not stale `circuit.components`
      simulator?.domain,
      freshCircuit.connections,
      notebookCells,
      sessionId,
      [comp.instanceId],
      authData?.role
    );

    const newCells = response.success ? response.data.cells : [];

    if (response.success && newCells.length > 0) {
      // Match the CLICKED cell specifically, not comp.instanceId — when
      // comp was redirected to the builder, comp.instanceId is the
      // builder's, but the clicked cellId may be a sub-canvas node's own
      // cell, which needs its own editor updated with its own code.
      const clickedCell = newCells.find(nc => `cell_${nc.instanceId}` === cellId);
      if (clickedCell) {
        notebookRef.current?.updateCellCode(cellId, clickedCell.code);
      }

      let updatedCells = notebookCells;
      newCells.forEach((nc) => {
        const ncId = `cell_${nc.instanceId}`;
        const exists = updatedCells.some(c => c.cellId === ncId);
        const merged = {
          ...nc,
          code:              nc.code,
          javascriptCode:    nc.javascriptCode || '',
          pythonCode:        nc.pythonCode || nc.code,
          isPyodideSkeleton: nc.isPyodideSkeleton || false,
          updatedAt:         new Date(),
        };
        updatedCells = exists
          ? updatedCells.map(c => c.cellId === ncId ? { ...c, ...merged } : c)
          : [...updatedCells, merged];
      });

      setNotebookCells(updatedCells);
      await saveNotebookCells(simulator._id, updatedCells);
      showNotification('Notebook cell has been regenerated!', 'success');
    }
  };

  const handlePostToAISession = async () => {
    const isGradedLab = sessionId && sessionId.includes('_LAB');
    if (!isGradedLab || !simulator) return;

    try {
      const duration = simulator?.state?.uptime || 0;

      // Collect AI evidence from notebookCells — matches CourseDomains.AI.gradingPayloadFields
      const cellOutputsFromCells = notebookCells.map(c => ({
        cellId:      c.cellId,
        componentId: c.componentId,
        label:       c.label || '',
        category:    c.category || '',
        executed:    !!(c.simOutputs?.stdout || c.simOutputs?.hasPlot),
        stdout:      c.simOutputs?.stdout  || '',
        error:       c.simOutputs?.error   || '',
        hasPlot:     c.simOutputs?.hasPlot || false,
      }));

      const gradingData = {
        cellOutputs:   cellOutputsFromCells,
        cellsExecuted: cellOutputsFromCells.filter(c => c.executed).length,
        totalCells:    notebookCells.length,
        hasErrors:     cellOutputsFromCells.some(c => !!c.error),
        stdoutSummary: cellOutputsFromCells.map(c => c.stdout).join('\n').slice(0, 2000),
      };

      const response = await saveGradingPayload(simulator._id, duration, gradingData);

      if (response.success) {
        setSimulator(prev => ({ ...prev, gradingPayload: response.data }));
        showNotification('Notebook submitted for grading!', 'success');
      } else {
        showNotification(`Failed to submit: ${response.error}`, 'error');
      }
    } catch (err) {
      showNotification('Failed to submit', 'error');
    }
  };

  // const handleGrade = async () => {
  //   await handlePostToAISession();  // saves payload with status='pending'
  //   setShowSimulationReport(true);  // then opens modal
  // };
  const handleGrade = async () => {
    // Optionally reset execution state before regrade
    if (simulator?._id && simulator?.gradingPayload) {
      await resetSimulator(simulator._id);
    }

    let freshSimulator = simulator;
    const res = await getSimulatorBySessionId(sessionId);
    if (res.success) {
      freshSimulator = res.data;
      setSimulator(res.data);
    }
    await handlePostToAISession();
    setShowSimulationReport(true);
  };

  const handleSubmitGrades = async () => {
    console.log('🔍 submitGrades — simulator._id:', simulator?._id, 'gradingPayload:', simulator?.gradingPayload);
    if (!simulator?._id) return;

    const response = await submitGrades(simulator._id);
    if (response.success) {
      setSimulator(prev => ({ ...prev, gradingPayload: response.data }));
      showNotification('Grades submitted!', 'success');
    } else {
      showNotification(`Failed to submit grades: ${response.error}`, 'error');
    }
  };

  // ── Header ────────────────────────────────────────────────────────────────
  const profileHeaderData = {
    ...authData,
    title: <h3>{CourseDomains[simulator?.domain]?.notebookTitle ?? '🧠 Notebook'}</h3>,
    // subTitle: circuit?.name || 'AI Notebook Simulator',
  };

  // ── Render ────────────────────────────────────────────────────────────────
  
  return (
    <div className="ai-notebook-page">

      <div className="page-header">
          <ProfileHeader data={profileHeaderData} activePage="simulator" />
          <div className="headerMiddle">
          {/* <img src="/images/prof_ix.png" alt="Logo" className="header-logo" /> */}
            <ProfIXChatbot 
              userName={authData.name}
              courseId={courseId} 
              courseName={courseInfo?.title} 
              category={courseInfo?.category}
            />
          </div>
          <SharedHeader notificationCount={5} icons={[]} />
      </div>

      {/* Toolbar */}
      <div className="page-toolbar">
        {/* <div className="ai-notebook-toolbar-left"> */}
          <BackButton 
              to="-1"
              activeTab=""
              label="←"
              bgColor="#6b7280"
              />
          {sessionId && sessionId.includes('_LAB') && (
            <>
              {/* <h3>{CourseDomains[simulator?.domain]?.notebookTitle ?? '🧠 Notebook'}</h3> */}
              {/* Show Report — only when gradingPayload exists */}
              {simulator?.gradingPayload && (
                <CustomButton
                  buttonTitle="📊 Show Report"
                  action={() => setShowSimulationReport(true)}
                  bgColor="#8b5cf6"
                  disabled={!simulator}
                />
              )}

              {/* Grade — always shown, disabled only when graded */}
              <CustomButton
                buttonTitle="📤 Grade"
                action={handleGrade}
                bgColor="#8b5cf6"
                disabled={
                  !simulator ||
                  simulator?.gradingPayload?.status === 'graded'
                }
              />
            </>
          )}
          <span className="page-toolbar-title-wrap">
            {circuit && circuit.labId && (
              <span className="page-toolbar-title">📚 {circuit.labId}</span>
            )}
            {circuit && circuit.name && (<span>🧠 {circuit.name}</span>)}
            <span className="page-toolbar-subtitle">Session: {sessionId}</span>
          </span>
        {/* </div> */}
      </div>

      {/* Main content — PyodideProvider wraps NotebookInner */}
      <NotebookProvider
      // <PyodideProvider
        // domain={simulator?.domain}
        executionContext={executionContext}
        executeNotebookCell={executeNotebookCell}
      >
        <div className="ai-notebook-content">
          {isLoading ? (
            <div className="loading-overlay">
              <div className="loader" />
              {/* <p>Loading AI notebook...</p> */}
            </div>
          ) : (
            <NotebookInner
              simulatorId={simulator?._id}
              ref={notebookRef}
              initialCells={notebookCells}
              domain={simulator?.domain}
              notebookTitle={circuit?.name || 'AI Notebook'}
              externalLoading={generationProgress}
              emptyMessage="No cells generated — go back to the canvas and sync your circuit"
              executionContext={executionContext} 
              onMove={async (orderedCellIds) => {
                if (!circuit?._id) return;
                const orderUpdates = orderedCellIds.map((cellId, index) => ({
                  instanceId: cellId.replace('cell_', ''),
                  notebookOrder: index,
                }));
                await updateNotebookOrder(circuit._id, orderUpdates);
              }}

              onSaveCell={(cellId, newCode, output, freshJs) => {
                if (!simulator) return;
                setNotebookCells(prev => {
                  const updatedCells = prev.map(c =>
                    c.cellId === cellId ? {
                      ...c,
                      code: newCode,
                      updatedAt: new Date(),
                      // Persist freshly-translated JS whenever it's provided
                      // (from a Run that auto-translated, or a manual Translate
                      // click) so it survives reload and isn't re-billed.
                      ...(freshJs !== undefined ? { javascriptCode: freshJs } : {}),
                      ...(output ? {
                        simOutputs: {
                          stdout:  output.stdout || '',
                          error:   output.error  || '',
                          hasPlot: !!output.plot,
                          ranAt:   new Date()
                        },
                        executionCount: (c.executionCount || 0) + 1
                      } : {})
                    } : c
                  );
                  saveNotebookCells(simulator._id, updatedCells);
                  return updatedCells;
                });
              }}

              onRegenerateCell={handleRegenerateCell}

              onCellOutput={(cellId, componentId, output) => {
                // Update grading payload state
                setCellOutputs(prev => {
                  const existing = prev.findIndex(c => c.cellId === cellId);
                  const entry = { cellId, componentId, executed: true, stdout: output.stdout, error: output.error };
                  if (existing >= 0) {
                    const updated = [...prev];
                    updated[existing] = entry;
                    return updated;
                  }
                  return [...prev, entry];
                });

                // Use functional update to always read latest notebookCells
                setNotebookCells(prev => {
                  const updatedCells = prev.map(c =>
                    c.cellId === cellId ? {
                      ...c,
                      simOutputs: {
                        stdout:  output.stdout || '',
                        error:   output.error  || '',
                        hasPlot: !!output.plot,
                        ranAt:   new Date()
                      },
                      executionCount: (c.executionCount || 0) + 1,
                      updatedAt: new Date()
                    } : c
                  );
                  // Save to DB inside the functional update — always has latest state
                  if (simulator?._id) {
                    saveNotebookCells(simulator._id, updatedCells);
                  }
                  return updatedCells;
                });
              }}

              onEditorMount={(cellId, editor) => {
                cellEditorRefs.current[cellId] = editor;
              }}

              // onTranslateCell={translateNotebookCells}
              onTranslateCell={async (cellId, pythonCode, componentId) => {
                const instanceId = cellId.replace('cell_', '');
                // Server now resolves circuitComponents/connections/allCells
                // itself from sessionId — don't pass circuit.components/
                // connections/notebookCells positionally, that's what was
                // landing in req.body.sessionId and crashing the cast.
                return translateNotebookCells(
                  cellId,
                  pythonCode,
                  componentId,
                  instanceId,
                  sessionId,
                  authData?.role
                );
              }}

              onRetryGenerateCell={retryGenerateCell}
            />
          )}
        </div>
      {/* </PyodideProvider> */}
      </NotebookProvider>

      {/* Generating overlay */}
      {generationProgress && (
        <div className="ai-loading-overlay">
          <div className="ai-loading-spinner" />
          <p className="ai-loading-title">🤖 Prof-iX is loading your notebook...</p>
          <p className="ai-loading-subtitle">🤖 Building cell {generationProgress.current} of {generationProgress.total}: {generationProgress.label}</p>
        </div>
      )}
      
      {generationProgress && (
        <div className="nb-generation-progress-banner">
          🤖 Building cell {generationProgress.current} of {generationProgress.total}: {generationProgress.label}
        </div>
      )}

      <SimulationReport
        visible={showSimulationReport}
        simulator={simulator}
        circuit={circuit}
        domain={simulator?.domain}
        onClose={() => setShowSimulationReport(false)}
        onSubmit={handleSubmitGrades}
        onRefreshSimulator={async () => {
          const res = await getSimulatorBySessionId(sessionId);
          if (res.success) setSimulator(res.data);
        }}
      />

      {circuitError && (
        <div className="circuit-error-container">
          ❌ {circuitError}
          <button onClick={clearCircuitError} className="circuit-error-close">✖</button>
        </div>
      )}

    </div>
  );
};

export default NotebookSimulator;